import { useLayoutEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx';
import { Button } from './Button';

export interface SaveBarProps {
  /** Whether there are unsaved changes (drives the message + button state). */
  dirty?: boolean;
  message?: ReactNode;
  saving?: boolean;
  /** Invalid changes can still be discarded; only saving is disabled. */
  saveDisabled?: boolean;
  saveLabel?: string;
  discardLabel?: string;
  onSave: () => void;
  onDiscard?: () => void;
  className?: string;
  /** Accessible name for the bar's region. English default. */
  regionLabel?: string;
}

/**
 * Save-bar with a dirty indicator (brief §4.2 / §7.8), pinned full-width to the
 * bottom of the SCREEN. Pairs with an unsaved-changes guard in the host page.
 *
 * Wherever it is rendered, it is portalled into one shared dock fixed to the
 * viewport's bottom edge. It used to be `position: sticky` in place, which only
 * sticks inside its own parent — rendered in a card, it stuck to that card and
 * sat under the form like any other row, out of sight on a long page. Portalled,
 * no ancestor's overflow or transform can trap it, and two dirty cards on one
 * page stack in the dock instead of drawing over each other.
 *
 * The dock publishes its height as `--tc-savebar-h` on the root element, which
 * the stylesheet adds to the body's bottom padding (so the end of the page can
 * still be scrolled clear of the bar) and to the toast offset.
 */
export function SaveBar({
  dirty = true,
  message = 'You have unsaved changes',
  saving = false,
  saveDisabled = false,
  saveLabel = 'Save changes',
  discardLabel = 'Discard',
  onSave,
  onDiscard,
  className,
  regionLabel = 'Unsaved changes',
}: SaveBarProps) {
  const dock = useSaveBarDock();
  if (!dock) return null;

  return createPortal(
    <div className={cx('tc-savebar', className)} role="region" aria-label={regionLabel}>
      <span className="tc-savebar__status">
        <span className="tc-status-dot tc-status-dot--warning" aria-hidden />
        {message}
      </span>
      <div className="tc-savebar__actions">
        {onDiscard && (
          <Button variant="secondary" onClick={onDiscard} disabled={saving || !dirty}>
            {discardLabel}
          </Button>
        )}
        <Button onClick={onSave} loading={saving} disabled={!dirty || saveDisabled}>
          {saveLabel}
        </Button>
      </div>
    </div>,
    dock,
  );
}

const HEIGHT_VAR = '--tc-savebar-h';

let dockEl: HTMLDivElement | null = null;
let dockObserver: ResizeObserver | null = null;
let dockUsers = 0;

/**
 * The shared dock, created when the first bar mounts and removed when the last
 * one unmounts — an empty dock left behind would still pad the page and, on a
 * phone, paint a strip over the home-indicator inset.
 */
function useSaveBarDock(): HTMLElement | null {
  const [dock, setDock] = useState<HTMLElement | null>(null);

  // A layout effect, so the first commit (which renders nothing) is replaced by
  // the portalled bar before the browser paints.
  useLayoutEffect(() => {
    setDock(acquireDock());
    return releaseDock;
  }, []);

  return dock;
}

function acquireDock(): HTMLDivElement {
  dockUsers += 1;
  if (dockEl) return dockEl;

  const el = document.createElement('div');
  el.className = 'tc-savebar-dock';
  document.body.appendChild(el);
  dockEl = el;

  const root = document.documentElement;
  const publish = () => root.style.setProperty(HEIGHT_VAR, `${el.offsetHeight}px`);
  if (typeof ResizeObserver !== 'undefined') {
    dockObserver = new ResizeObserver(publish);
    dockObserver.observe(el);
  }
  publish();
  return el;
}

function releaseDock(): void {
  dockUsers -= 1;
  if (dockUsers > 0 || !dockEl) return;
  dockObserver?.disconnect();
  dockObserver = null;
  dockEl.remove();
  dockEl = null;
  document.documentElement.style.removeProperty(HEIGHT_VAR);
}
